import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { DropdownMenu } from 'radix-ui';
import { Building, Check, ChevronsUpDown, Plus } from 'lucide-react';
import { AREA_HOME, areaOfPath, useCurrentOrg, type Area } from '@/auth/roles';
import { cn } from '@/lib/cn';

export function AreaSwitcher({ areas }: { areas: Area[] }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const current = areaOfPath(pathname);
  if (areas.length <= 1) return null;
  return (
    <nav aria-label={t('shell.areas')} className="grid gap-0.5 rounded-[var(--radius-ctl)] border border-line bg-surface-2 p-0.5" style={{ gridTemplateColumns: `repeat(${areas.length > 3 ? 2 : areas.length}, minmax(0, 1fr))` }}>
      {areas.map((a) => (
        <Link
          key={a}
          to={AREA_HOME[a]}
          aria-current={current === a ? 'page' : undefined}
          className={cn(
            'truncate rounded-[3px] px-1.5 py-1 text-center text-xs font-semibold transition-colors',
            current === a ? 'bg-surface text-ink shadow-[0_0_0_1px_var(--line)]' : 'text-ink-3 hover:text-ink',
          )}
        >
          {t(`area.${a}`)}
        </Link>
      ))}
    </nav>
  );
}

export function OrgSwitcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { org, organizations, select } = useCurrentOrg();
  if (!org) return null;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex w-full min-w-0 items-center gap-2 rounded-[var(--radius-ctl)] border border-line bg-surface px-2 py-1.5 text-left hover:border-line-strong"
          aria-label={t('shell.switchOrg')}
        >
          <Building className="size-4 shrink-0 text-ink-3" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{org.name}</span>
            <span className="block text-[11px] text-ink-3">{t(`orgRole.${org.role}`)}</span>
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-ink-3" aria-hidden />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" sideOffset={4} className="z-50 w-64 rounded-[var(--radius-panel)] border border-line bg-surface p-1 shadow-[var(--shadow-pop)]">
          <DropdownMenu.Label className="px-2.5 pb-1 pt-2 text-xs font-semibold text-ink-3">{t('shell.organizations')}</DropdownMenu.Label>
          {organizations.map((o) => (
            <DropdownMenu.Item
              key={o.id}
              onSelect={() => {
                select(o.id);
                navigate('/projects');
              }}
              className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-ctl)] px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-surface-3"
            >
              <span className="w-4">{o.id === org.id && <Check className="size-4 text-accent" />}</span>
              <span className="min-w-0 flex-1 truncate">{o.name}</span>
              <span className="text-xs text-ink-3">{t(`orgRole.${o.role}`)}</span>
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Item
            onSelect={() => navigate('/org/new')}
            className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-ctl)] px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-surface-3"
          >
            <Plus className="size-4 text-ink-3" />
            {t('shell.newOrg')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
